// Zero-markup editing ("Level 0"): finds editable text, links, images and videos on ANY page and gives each a stable key
// derived from its ORIGINAL content, so overrides can be re-applied on every load without touching the site's code.
//   key = auto:<scope>:<hash of original text>:<occurrence>     scope = page path, or "g" for header/footer/nav (shared by all pages)
// No DOM mutation other than marking elements (data-hcms-k) and applying overrides.
import { normPath, safeCssUrl } from '../shared/render';

export type AutoKind = 'text' | 'inline' | 'img' | 'link' | 'video';
export interface AutoItem { el: HTMLElement; key: string; kind: AutoKind; }

const TEXT_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'LI', 'BLOCKQUOTE', 'FIGCAPTION', 'SUMMARY', 'DT', 'DD', 'TD', 'TH', 'LABEL', 'BUTTON', 'A', 'SPAN', 'DIV', 'SMALL', 'STRONG', 'EM', 'B', 'I', 'CITE', 'TIME', 'LEGEND', 'CAPTION', 'ADDRESS']);
const INLINE_OK = new Set(['A', 'B', 'STRONG', 'I', 'EM', 'BR', 'SPAN', 'MARK', 'U', 'SUB', 'SUP', 'SMALL', 'S', 'ABBR', 'TIME']);
const SKIP = 'script,style,noscript,svg,iframe,select,textarea,input,code,pre,canvas,template,#hydracms-host,[contenteditable],[data-hcms-ignore],[data-cms-ignore],[aria-hidden="true"],[hidden]';

export function hash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(36);
}
const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
const hasWord = (s: string) => /[\p{L}\p{N}]/u.test(s);

export function scopeOf(el: Element, path = location.pathname): string {
  return el.closest('header,footer,nav,[role="banner"],[role="contentinfo"],[role="navigation"]') ? 'g' : normPath(path);
}

function inlineOnly(el: Element): boolean {
  for (const c of Array.from(el.children)) {
    if (!INLINE_OK.has(c.tagName)) return false;
    if (!inlineOnly(c)) return false;
  }
  return true;
}
const hasExplicit = (el: Element) => Array.from(el.attributes).some((a) => a.name.startsWith('data-cms'));
/** Elements the site keyed itself (Level 1/2). A block holding one of these is not auto-editable as a whole, or the two would overlap. */
const EXPLICIT = '[data-cms],[data-cms-field],[data-cms-img],[data-cms-link],[data-cms-field-link],[data-cms-video],[data-cms-bg]';

function textCandidate(el: HTMLElement): 'text' | 'inline' | null {
  if (!TEXT_TAGS.has(el.tagName)) return null;
  if (hasExplicit(el) || el.querySelector(EXPLICIT)) return null;
  const t = norm(el.textContent ?? '');
  if (t.length < 2 || t.length > 4000 || !hasWord(t)) return null;
  if (!inlineOnly(el)) return null;
  return el.children.length ? 'inline' : 'text';
}

/** A link/button whose only text is a run of bare text next to decorative children (an arrow svg, an icon): returns the text nodes (React may split one string in two). */
function labelOf(el: HTMLElement): Text[] | null {
  if (!TEXT_TAGS.has(el.tagName) || hasExplicit(el) || !el.children.length) return null;
  if (Array.from(el.children).some((c) => norm(c.textContent ?? '') !== '' || c.hasAttribute('data-hcms-k'))) return null;
  const kids = Array.from(el.childNodes);
  const idx = kids.map((n, i) => (n.nodeType === 3 && norm(n.textContent ?? '') !== '' ? i : -1)).filter((i) => i >= 0);
  if (!idx.length) return null;
  const run = kids.slice(idx[0], idx[idx.length - 1] + 1);
  if (run.some((n) => n.nodeType === 1)) return null; // a decorative element sits between the words
  const texts = run.filter((n): n is Text => n.nodeType === 3);
  const t = norm(texts.map((n) => n.textContent).join(''));
  return t.length >= 2 && t.length <= 400 && hasWord(t) ? texts : null;
}
/** Wrap the visible words in a span (surrounding spaces stay outside) so they can be edited without touching the icon. */
function wrapLabel(texts: Text[]): HTMLElement {
  const first = texts[0], last = texts[texts.length - 1], parent = first.parentNode!;
  const lead = /^\s*/.exec(first.textContent ?? '')![0], trail = /\s*$/.exec(last.textContent ?? '')![0];
  const span = document.createElement('span');
  let n: ChildNode | null = first; const run: ChildNode[] = [];
  while (n) { run.push(n); if (n === last) break; n = n.nextSibling; }
  parent.insertBefore(span, first);
  run.forEach((x) => span.appendChild(x));
  const core = norm(span.textContent ?? '');
  span.textContent = (span.textContent ?? '').trim().replace(/\s+/g, ' ') || core;
  if (lead) parent.insertBefore(document.createTextNode(lead), span);
  if (trail) parent.insertBefore(document.createTextNode(trail), span.nextSibling);
  return span;
}

/** If an element's only content is one link/button/span, edit that inner element instead (keeps the anchor intact). */
function descend(el: HTMLElement): HTMLElement {
  const kids = Array.from(el.children);
  if (kids.length === 1 && ['A', 'BUTTON', 'SPAN'].includes(kids[0].tagName) && norm(el.textContent ?? '') === norm(kids[0].textContent ?? '')) return descend(kids[0] as HTMLElement);
  return el;
}

const srcKey = (src: string) => { try { return new URL(src, location.href).pathname; } catch { return src; } };

/** Find every editable thing under root, marking new ones. Already-marked elements keep their key. */
export function discover(root: ParentNode = document.body, has?: (key: string) => boolean): AutoItem[] {
  const items: AutoItem[] = [];
  const counts = new Map<string, number>();
  const take = (base: string) => { const n = (counts.get(base) ?? 0) + 1; counts.set(base, n); return `${base}:${n}`; };
  const walk = (node: Element) => {
    if (node.matches(SKIP)) return;
    const el = node as HTMLElement;
    const known = el.getAttribute('data-hcms-k');
    if (known) { items.push({ el, key: known, kind: el.getAttribute('data-hcms-t') as AutoKind }); const base = known.replace(/:\d+$/, ''); counts.set(base, Math.max(counts.get(base) ?? 0, +known.slice(known.lastIndexOf(':') + 1)));
      const lk0 = el.getAttribute('data-hcms-lk'); // a marked text element can also be a link
      if (lk0) { items.push({ el, key: lk0, kind: 'link' }); const b2 = lk0.replace(/:\d+$/, ''); counts.set(b2, Math.max(counts.get(b2) ?? 0, +lk0.slice(lk0.lastIndexOf(':') + 1))); }
      return; }
    if (el.tagName === 'IMG') {
      const src = el.getAttribute('src') ?? '';
      const w = el.getBoundingClientRect().width || +(el.getAttribute('width') ?? 0) || (el as HTMLImageElement).naturalWidth || 0; // unloaded images have no box yet
      if (src && !src.startsWith('data:') && (!w || w >= 48)) {
        const key = take(`auto:img:${hash(srcKey(src))}`); mark(el, key, 'img'); items.push({ el, key, kind: 'img' });
      }
      return;
    }
    if (el.tagName === 'VIDEO' || el.tagName === 'IFRAME') {
      const src = el.getAttribute('src') ?? el.querySelector('source')?.getAttribute('src') ?? '';
      if (el.tagName === 'IFRAME' ? /youtube|vimeo/.test(src) : !!src) { const key = take(`auto:vid:${hash(srcKey(src))}`); mark(el, key, 'video'); items.push({ el, key, kind: 'video' }); }
      return;
    }
    const lab = labelOf(el);
    if (lab) {
      const key = take(`auto:${scopeOf(el)}:${hash(norm(lab.map((n) => n.textContent).join('')))}`);
      if (!has || has(key)) { const span = wrapLabel(lab); mark(span, key, 'text'); items.push({ el: span, key, kind: 'text' }); }
      if (el.tagName === 'A' && el.getAttribute('href')) linkOf(el, items, take);
      return;
    }
    const kind = textCandidate(el);
    if (kind) {
      const target = descend(el);
      if (target !== el && target.hasAttribute('data-hcms-k')) { walk(target); return; } // already marked on an earlier pass
      const k2 = target === el ? kind : textCandidate(target);
      if (k2) {
        const key = take(`auto:${scopeOf(target)}:${hash(norm(target.textContent ?? ''))}`);
        mark(target, key, k2); items.push({ el: target, key, kind: k2 });
        if (target.tagName === 'A' && target.getAttribute('href')) linkOf(target, items, take);
        return;
      }
    }
    if (el.tagName === 'A' && el.getAttribute('href')) {
      const lk = el.getAttribute('data-hcms-lk');
      if (lk) { items.push({ el, key: lk, kind: 'link' }); const base = lk.replace(/:\d+$/, ''); counts.set(base, Math.max(counts.get(base) ?? 0, +lk.slice(lk.lastIndexOf(':') + 1))); }
      else linkOf(el, items, take);
    }
    for (const c of Array.from(el.children)) walk(c);
  };
  for (const c of Array.from((root as Element).children ?? [])) walk(c);
  return items;
}
function linkOf(el: HTMLElement, items: AutoItem[], take: (b: string) => string) {
  if (el.hasAttribute('data-hcms-lk') || el.matches('[data-cms-link],[data-cms-field-link]')) return; // the site keyed this link itself
  const href = el.getAttribute('href') ?? '';
  if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
  const key = take(`auto:a:${hash(href + '|' + norm(el.textContent ?? ''))}`);
  el.setAttribute('data-hcms-lk', key);
  items.push({ el, key, kind: 'link' });
}
function mark(el: HTMLElement, key: string, kind: AutoKind) { el.setAttribute('data-hcms-k', key); el.setAttribute('data-hcms-t', kind); }

// ---------- apply ----------
type Fields = Record<string, { t: string; v: any }>;

export function applyOne(it: AutoItem, f: { t: string; v: any } | undefined) {
  const { el, kind } = it;
  if (!f) return;
  if (kind === 'text') { if (typeof f.v === 'string' && el.textContent !== f.v) el.textContent = f.v; }
  else if (kind === 'inline') { if (typeof f.v === 'string' && el.innerHTML !== f.v) el.innerHTML = f.v; }
  else if (kind === 'img' && f.v?.src) {
    el.parentElement?.tagName === 'PICTURE' && el.parentElement.querySelectorAll('source').forEach((s) => s.remove());
    el.removeAttribute('srcset'); el.removeAttribute('sizes');
    if (el.getAttribute('src') !== f.v.src) el.setAttribute('src', f.v.src);
    if (f.v.alt !== undefined) el.setAttribute('alt', f.v.alt);
  } else if (kind === 'video' && f.v) {
    if (f.v.kind === 'youtube' && el.tagName === 'IFRAME') el.setAttribute('src', `https://www.youtube-nocookie.com/embed/${f.v.id}`);
    else if (f.v.kind === 'vimeo' && el.tagName === 'IFRAME') el.setAttribute('src', `https://player.vimeo.com/video/${f.v.id}`);
    else if (f.v.kind === 'file' && el.tagName === 'VIDEO') { el.querySelectorAll('source').forEach((s) => s.remove()); el.setAttribute('src', f.v.src); (el as HTMLVideoElement).load?.(); }
  }
}

/** Apply every override for this page to the DOM. Safe to call repeatedly. */
export function applyAuto(fields: Fields, root: ParentNode = document.body): number {
  let n = 0;
  for (const it of discover(root, (k) => !!fields[k])) {
    if (it.kind === 'link') continue;
    const f = fields[it.key];
    if (f) { applyOne(it, f); n++; }
  }
  // decorative copies (a looping logo strip repeats its pictures as aria-hidden) follow the picture they copy
  document.querySelectorAll<HTMLImageElement>('[aria-hidden="true"] img, img[aria-hidden="true"]').forEach((img) => {
    if (img.closest('#hydracms-host') || img.hasAttribute('data-hcms-k')) return;
    const src = img.getAttribute('src'); if (!src || src.startsWith('data:')) return;
    const f = fields[`auto:img:${hash(srcKey(src))}:1`];
    if (f?.v?.src) { applyOne({ el: img, key: '', kind: 'img' }, f); n++; }
  });
  document.querySelectorAll<HTMLElement>('[data-hcms-lk]').forEach((a) => {
    const f = fields[a.getAttribute('data-hcms-lk')!];
    if (f && typeof f.v === 'string' && a.getAttribute('href') !== f.v) { a.setAttribute('href', f.v); n++; }
  });
  return n;
}

/** SEO panel values for this page (title, description, share picture, noindex) applied client-side. */
export function applySeo(fields: Fields, path = location.pathname) {
  const p = normPath(path);
  const v = (k: string) => fields[`seo:${p}:${k}`]?.v;
  const title = v('title'), desc = v('description'), img = v('image');
  const meta = (sel: string, name: string, attr: 'name' | 'property') => {
    let m = document.head.querySelector<HTMLMetaElement>(sel);
    if (!m) { m = document.createElement('meta'); m.setAttribute(attr, name); document.head.append(m); }
    return m;
  };
  if (title) { document.title = title; meta('meta[property="og:title"]', 'og:title', 'property').content = title; meta('meta[name="twitter:title"]', 'twitter:title', 'name').content = title; }
  if (desc) { for (const [s, n, a] of [['meta[name="description"]', 'description', 'name'], ['meta[property="og:description"]', 'og:description', 'property'], ['meta[name="twitter:description"]', 'twitter:description', 'name']] as const) meta(s, n, a).content = desc; }
  if (img) { const abs = img.startsWith('/') ? location.origin + img : img; meta('meta[property="og:image"]', 'og:image', 'property').content = abs; meta('meta[name="twitter:image"]', 'twitter:image', 'name').content = abs; }
  if (v('noindex')) meta('meta[name="robots"]', 'robots', 'name').content = 'noindex, nofollow';
}
export { safeCssUrl };
